// src/hooks/useCalculatorForm.js
//
// State management for the Guided IRPF Calculator.
// Mirrors useTriageForm.js shape and conventions — same step/progress
// pattern, same set()/toggleMulti() helpers — but produces a
// CalculatorSession-shaped payload (see _shared/calculators/calculatorTypes.ts)
// rather than triage answers.
//
// STEP STRUCTURE:
//   1 — Your Spain situation     (profile_facts, tax_year_facts)
//   2 — Your income               (income_items)
//   3 — Adjustments                (adjustments, dependents)
//   4 — Your estimate              (result — calls edge function)
//
// This hook owns form state and step navigation only. The actual
// calculation happens server-side (calculateStandaloneIrpf via edge
// function) on step 4 — this hook does not import calculator logic.
//
// SESSION LIFECYCLE (draft/publish model):
//   sessionId tracks the calculator_sessions row id across recalculations.
//   The first runCalculation() call sends no session_id, so calculate-irpf
//   inserts a new draft row and returns its id, stored here. Every
//   subsequent runCalculation() call (Back, edit, recalculate) sends that
//   same sessionId, so the edge function updates the row in place instead
//   of creating a new one each time.
//
//   published/publishSession are the explicit publish step: calling
//   publishSession() hits the separate publish-session edge function,
//   which mints public_token and sets published_at on the row. Nothing is
//   publicly shareable or immutable until that happens — calculating never
//   publishes on its own.

import { useState, useMemo } from 'react'
import posthog from '../lib/posthog'
import { getFirstReferrer, getDeviceType } from '../lib/clientMeta'
import { slots, getSlotOptions } from '../lib/slotConfig'

// ── Bracket year ──────────────────────────────────────────────────────────
// Single source of truth for which tax year the calculator estimates.
// No longer asked as a question — 2025 and 2026 use identical brackets,
// and asking adds friction without changing any output.
// Update this constant when new bracket schedules are published.
export const CURRENT_BRACKET_YEAR = 2026

// ── Comunidad options ─────────────────────────────────────────────────────
// Matches getRegionalFactor() in irpfCore.ts — only comunidades with known
// regional adjustments are listed first, others grouped below.

export const COMUNIDAD_OPTIONS = [
  { value: 'andalucia',        label: 'Andalucía' },
  { value: 'madrid',           label: 'Madrid' },
  { value: 'cataluna',         label: 'Cataluña' },
  { value: 'valencia',         label: 'Comunidad Valenciana' },
  { value: 'galicia',          label: 'Galicia' },
  { value: 'castilla_leon',    label: 'Castilla y León' },
  { value: 'castilla_mancha',  label: 'Castilla-La Mancha' },
  { value: 'pais_vasco',       label: 'País Vasco' },
  { value: 'canarias',         label: 'Canarias' },
  { value: 'baleares',         label: 'Islas Baleares' },
  { value: 'aragon',           label: 'Aragón' },
  { value: 'murcia',           label: 'Murcia' },
  { value: 'asturias',         label: 'Asturias' },
  { value: 'navarra',          label: 'Navarra' },
  { value: 'extremadura',      label: 'Extremadura' },
  { value: 'cantabria',         label: 'Cantabria' },
  { value: 'la_rioja',          label: 'La Rioja' },
]

const FORAL_COMUNIDADES = new Set(['pais_vasco', 'navarra'])

// ── Age options ────────────────────────────────────────────────────────────
// Single source of truth for both the primary taxpayer's age question and
// the spouse's age question — so they can never drift apart from each other.
export const AGE_OPTIONS = [
  { value: 64,  label: 'Under 65' },
  { value: 67,  label: '65 to 74' },
  { value: 76,  label: '75 or older' },
]

// Returns { reason, cta } if this field's current answer disqualifies
// ordinary IRPF, or null otherwise. Exported so CalculatorPage can render
// an inline alert directly under the row that caused it, instead of a
// separate full-screen block that hides the user's other answers.
export function getEligibilityBlock(fieldKey, answers) {
  if (fieldKey === 'comunidad') {
    const { comunidad } = answers
    if (comunidad && FORAL_COMUNIDADES.has(comunidad)) {
      const name = comunidad === 'pais_vasco' ? 'País Vasco' : 'Navarra'
      return {
        reason: `${name} is a foral territory with its own tax authority and rates entirely separate from the national LIRPF system. This calculator does not yet support foral territory tax — a local asesor fiscal is strongly recommended.`,
        cta: 'Get the full HolaSam assessment →',
      }
    }
    return null
  }
  if (fieldKey === 'residency') {
    if (answers.residency_year_context === 'non_resident') {
      return {
        reason: 'IRPF applies to Spanish tax residents only. Non-residents are generally taxed under IRNR (Impuesto sobre la Renta de No Residentes, Modelo 210), which uses different rules and rates. This calculator cannot produce a reliable estimate for your situation.',
        cta: 'Get the full HolaSam assessment →',
      }
    }
    return null
  }
  if (fieldKey === 'beckham_status') {
    if (answers.beckham_status === 'active') {
      return {
        reason: 'The Beckham Law (régimen especial de trabajadores desplazados) replaces ordinary IRPF with a flat 24% rate on Spanish-source income under IRNR rules. This calculator estimates ordinary IRPF and cannot produce a reliable figure for Beckham-enrolled taxpayers.',
        cta: 'Get the full HolaSam assessment →',
      }
    }
    return null
  }
  return null
}

// ── Step 1 field config ────────────────────────────────────────────────────
// Single/multi-select steps follow the same { question, hint, field, options }
// shape as STEP_CONFIG in useTriageForm.js, so the same OptionButton-driven
// rendering works here.

export const STEP_1_CONFIG = {
  eligibility: {
    question: 'A few quick eligibility checks',
    hint: 'These determine whether this calculator can apply to your situation.',
    field: null,
    type: 'eligibility_group',
    // Order these are rendered in, within the combined screen.
    fields: ['beckham_status', 'comunidad', 'residency'],
  },
  beckham_status: {
    question: 'Are you enrolled in the Beckham Law special regime?',
    shortLabel: 'Are you enrolled in Beckham Law regime? (most are not)',
    hint: "Beckham Law uses a different tax regime than ordinary IRPF.",
    field: 'beckham_status',
    type: 'single_select',
    options: [
      { value: 'not_active', label: 'No' },
      { value: 'active',     label: 'Yes' },
    ],
  },
  tax_year: {
    question: 'Which tax year are you estimating?',
    hint: '2025 and 2026 use 2025 IRPF rates (the most recently published schedule).',
    field: 'tax_year',
    type: 'single_select',
    options: [
      { value: 2025, label: '2025' },
      { value: 2026, label: '2026' },
    ],
  },
  comunidad: {
    question: 'Which comunidad autónoma do you live in?',
    shortLabel: 'Which autonomous community do you live in?',
    hint: "Used to estimate your regional IRPF rate and identify foral territories we don’t yet support.",
    field: 'comunidad',
    type: 'select_dropdown',
    options: COMUNIDAD_OPTIONS,
  },
  residency: {
    question: 'Were you a Spanish tax resident this year?',
    shortLabel: 'Are you a Spanish tax resident?',
    hint: 'Spain generally considers you a tax resident if you spent more than 183 days in Spain during the calendar year, or if your main economic interests are based here.',
    field: 'residency_year_context',
    type: 'single_select',
    options: [
      { value: 'full_year_resident', label: 'Yes' },
      { value: 'non_resident',       label: 'No' },
    ],
  },
  // Sourced from slotConfig.js's canonical citizenship_status/eu_citizen
  // slots — the same ones triage uses — rather than a locally-typed
  // yes/no list, so the two products can never drift on what "US
  // citizen" / "Green Card" mean. is_us_person is DERIVED from these
  // answers (see derivedIsUsPerson below), never asked directly.
  citizenship: {
    question: slots.citizenship_status.label,
    hint: slots.citizenship_status.hint,
    field: 'citizenship_status',
    type: 'citizenship_group',
    options: getSlotOptions('citizenship_status'),
    // Shown only when the primary answer is 'eu_citizen' — catches dual
    // citizens who'd otherwise answer "EU" and get silently missed.
    followUp: {
      field: 'eu_citizen',
      triggerValue: 'eu_citizen',
      question: slots.eu_citizen.label,
      hint: slots.eu_citizen.hint,
      options: getSlotOptions('eu_citizen'),
    },
    // Spouse column — same two-column pattern as the age question, and
    // same canonical options. No triage equivalent exists yet (triage is
    // single-subject), but sourcing from the same slot keeps the two
    // products' vocabulary in sync if that ever changes.
    spouseField: 'spouse_citizenship_status',
    spouseQuestion: "Spouse's citizenship status",
    spouseHint: '',
    spouseFollowUp: {
      field: 'spouse_eu_citizen',
      triggerValue: 'eu_citizen',
      question: 'Does your spouse also hold US citizenship or a Green Card?',
      hint: slots.eu_citizen.hint,
      options: getSlotOptions('eu_citizen'),
    },
  },
  age: {
    question: 'Which age bracket are you in?',
    hint: 'Spain applies a higher personal tax-free minimum at age 65 and again at 75.',
    field: 'age',
    type: 'age_group',
    options: AGE_OPTIONS,
    // Spouse's age is only rendered when marital_status === 'married'.
    // Asked here (rather than later on the income screen) because it's one
    // extra tap for married users who never end up adding spouse income —
    // cheaper than a demographic question mid-way through income entry.
    spouseField:    'spouse_age',
    spouseQuestion: "Spouse's age",
    spouseHint:     "",
  },
  marital_status: {
    question: "What's your marital status?",
    hint: "Married users can choose between an individual or joint filing orientation estimate. Civil union / partner users get an individual estimate. Joint filing rules for civil unions are unsettled in Spain.",
    field: 'marital_status',
    type: 'single_select',
    options: [
      { value: 'single',       label: 'Single, separated, widowed, or divorced' },
      { value: 'married',      label: 'Married' },
      { value: 'civil_union',  label: 'Civil union or registered partner' },
    ],
  },
  dependents: {
    question: 'Do you have any dependents living with you?',
    hint: "Spain increases your personal tax-free minimum for children under 25 and elderly family members in your household. Leave at 0 if none apply.",
    field: null,
    type: 'dependents',
  },
}

// Step order. The eligibility group replaces the old separate
// beckham/comunidad/residency steps. Whose income belongs to whom is now
// answered per-item on each income card in Step 2 (owner: 'self' | 'spouse'),
// not as an upfront question — see filingComparison.ts, which recomputes the
// individual-vs-joint comparison from those tags rather than from a
// pre-selected filing mode.
// tax_year is no longer asked — hardcoded to CURRENT_BRACKET_YEAR since both
// 2025 and 2026 use identical brackets.
// citizenship sits right after age — same two-column self/spouse pattern,
// and like age it's cheap for a married user to answer once up front even
// if they never end up adding spouse income.
export const STEP_1_ORDER = [
  'eligibility',
  'marital_status',
  'age',
  'citizenship',
  'dependents',
]

// Kept as a function (rather than a flat export) since other parts of the
// app historically called getStep1Order(answers) — order is now static,
// but the signature is preserved so callers don't need to change.
export function getStep1Order() {
  return STEP_1_ORDER
}

// ── Default form state ────────────────────────────────────────────────────
// Shape mirrors CalculatorSession's profile_facts / tax_year_facts so this
// can be assembled directly into the payload sent to the edge function.

const DEFAULT_ANSWERS = {
  // profile_facts
  // citizenships (ISO country array) is a separate, never-wired legacy
  // field — kept as-is, out of scope for this change.
  citizenships: [],
  citizenship_status: null,
  // eu_citizen only applies when citizenship_status === 'eu_citizen'.
  eu_citizen: null,
  // is_us_person is not user-set — derived from citizenship_status/
  // eu_citizen in derivedIsUsPerson. Not stored in answers at all; profileFacts
  // reads the derived value directly.
  marital_status: null,
  age: null,
  spouse_age: null,
  spouse_citizenship_status: null,
  spouse_eu_citizen: null,
  // Set by SpouseIncomeGate: 'added' if they chose "Yes, add their income",
  // 'skipped' if "No, continue". null until the gate is answered. Used only
  // for back-navigation (whether step2b has anything to show) — does not
  // affect the calculation itself, which reads owner-tagged items directly.
  spouse_income_choice: null,
  comunidad: null,
  beckham_status: null,

  // tax_year_facts
  // tax_year is no longer user-set — hardcoded to CURRENT_BRACKET_YEAR.
  tax_year: CURRENT_BRACKET_YEAR,
  residency_year_context: null,
}

// ── Default FX rates ──────────────────────────────────────────────────────
// HolaSam planning rates — must match STATIC_EXCHANGE_RATES in irpfStandalone.ts.
// These are the defaults shown in Step 3 before any user override.
export const DEFAULT_FX_RATES = {
  USD: 0.92,
  GBP: 1.17,
}

// ── Default adjustments / dependents state ────────────────────────────────
// Shapes mirror AdjustmentFacts / DependentFacts in calculatorTypes.ts.
// Stored as strings in form state (input fields), converted to numbers
// when building the session payload — same pattern as 'age' in answers.

const DEFAULT_ADJUSTMENTS = {
  pension_contributions: '',
  quarterly_payments: '',
  additional_withholdings: '',
}

const DEFAULT_DEPENDENTS = {
  children_under_25: '',
  children_under_3: '',
  ascendants_over_65: '',
  ascendants_over_75: '',
  disability_taxpayer: false,
}

export function useCalculatorForm() {
  const [answers, setAnswers] = useState(DEFAULT_ANSWERS)
  const [incomeItemsByType, setIncomeItemsByType] = useState({}) // { [income_type]: IncomeItem[] }
  const [adjustmentsForm, setAdjustmentsForm] = useState(DEFAULT_ADJUSTMENTS)
  const [dependentsForm, setDependentsForm] = useState(DEFAULT_DEPENDENTS)
  // fxRateOverrides: { [CURRENCY]: number } — only populated when user changes a rate in Step 3.
  // Currencies not present here use the DEFAULT_FX_RATES value.
  const [fxRateOverrides, setFxRateOverrides] = useState({})
  // autonomoFactsForm: null for pure-IRPF sessions (CalculatorPage.js never
  // touches this). AutonomoCalculatorPage.js initializes it with
  // createEmptyAutonomoFacts() on mount. buildSession() includes
  // autonomo_facts only when this is non-null — matching the "absent, not
  // null" contract documented on CalculatorSession in calculatorTypes.ts.
  const [autonomoFactsForm, setAutonomoFactsForm] = useState(null)
  const [autonomoResult, setAutonomoResult] = useState(null)
  const [filingComparison, setFilingComparison] = useState(null)
  const [autonomoIrpfImpact, setAutonomoIrpfImpact] = useState(null)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [result, setResult] = useState(null)
  const [savedToken, setSavedToken] = useState(null) // public_token, only set once published
  // sessionId: the calculator_sessions row id. Set from the first
  // runCalculation() response (insert) and sent back on every subsequent
  // call (update-in-place) — this is what actually stops a new row being
  // created on every Back+recalculate. Distinct from savedToken/public_token,
  // which only exists once the draft has been explicitly published.
  const [sessionId, setSessionId] = useState(null)
  // published: whether the current session has been published (public_token
  // + published_at set on the row). Drives the sidebar's "Draft autosaved"
  // vs. "Saved to your account ✓" states in CalculatorResult.js.
  const [published, setPublished] = useState(false)

  function set(field, value) {
    setAnswers(a => {
      const next = { ...a, [field]: value }
      // If marital_status changes away from 'married', clear spouse-owned
      // answers so a stale value doesn't silently affect a subsequent estimate.
      if (field === 'marital_status' && value !== 'married') {
        next.spouse_age = null
        next.spouse_income_choice = null
        next.spouse_citizenship_status = null
        next.spouse_eu_citizen = null
      }
      // If the primary citizenship answer changes away from 'eu_citizen',
      // clear the eu_citizen follow-up so a stale "yes, also US" answer from
      // a previous selection doesn't linger once it's no longer asked.
      if (field === 'citizenship_status' && value !== 'eu_citizen') {
        next.eu_citizen = null
      }
      if (field === 'spouse_citizenship_status' && value !== 'eu_citizen') {
        next.spouse_eu_citizen = null
      }
      return next
    })
  }

  function setIncomeItems(incomeType, items) {
    setIncomeItemsByType(prev => ({ ...prev, [incomeType]: items }))
  }

  // Flat list across all income types — this is what gets sent in the
  // CalculatorSession payload as income_items.
  const allIncomeItems = useMemo(
    () => Object.values(incomeItemsByType).flat(),
    [incomeItemsByType]
  )

  const hasAnyIncomeItems = allIncomeItems.length > 0

  // Derive is_us_person from the citizenship_status/eu_citizen answers —
  // store the fact (what citizenship status they picked), derive the
  // conclusion (whether that makes them a US person for tax purposes).
  // 'other' never implies is_us_person — a foreign national who happens to
  // also be a naturalized US citizen isn't caught by this question, same
  // gap that exists in triage's citizenship_status today; not fixed here.
  const derivedIsUsPerson = useMemo(() => (
    answers.citizenship_status === 'us_citizen' ||
    answers.citizenship_status === 'green_card' ||
    (answers.citizenship_status === 'eu_citizen' && answers.eu_citizen === true)
  ), [answers.citizenship_status, answers.eu_citizen])

  // Same derivation for the spouse — consumed by filingComparison.ts instead
  // of the hardcoded `false` it used to assume for the spouse's scenario.
  const derivedSpouseIsUsPerson = useMemo(() => (
    answers.spouse_citizenship_status === 'us_citizen' ||
    answers.spouse_citizenship_status === 'green_card' ||
    (answers.spouse_citizenship_status === 'eu_citizen' && answers.spouse_eu_citizen === true)
  ), [answers.spouse_citizenship_status, answers.spouse_eu_citizen])

  const currentStep1Order = getStep1Order()
  const safeStepIndex = Math.min(currentStepIndex, currentStep1Order.length - 1)

  const currentFieldKey = currentStep1Order[safeStepIndex]
  const currentStep = STEP_1_CONFIG[currentFieldKey]
  const totalSteps = currentStep1Order.length
  const isLastStep = safeStepIndex === totalSteps - 1

  const progressPct = useMemo(
    () => Math.round(((safeStepIndex + 1) / totalSteps) * 100),
    [safeStepIndex, totalSteps]
  )

  // ── Validation ─────────────────────────────────────────────────────────
  const canProceed = useMemo(() => {
    if (!currentStep) return false
    const val = answers[currentStep.field]

    switch (currentStep.type) {
      case 'single_select':
        return val !== null && val !== undefined
      case 'select_dropdown':
        return !!val
      case 'number_input':
        return val !== null && val !== '' && !Number.isNaN(Number(val))
      case 'age_group':
        if (val === null || val === undefined) return false
        if (answers.marital_status === 'married' &&
            (answers.spouse_age === null || answers.spouse_age === undefined)) {
          return false
        }
        return true
      case 'eligibility_group':
        return currentStep.fields.every(key => {
          const f = STEP_1_CONFIG[key].field
          const v = answers[f]
          const answered = v !== null && v !== undefined && v !== ''
          return answered && !getEligibilityBlock(key, answers)
        })
      case 'citizenship_group': {
        const selfAnswered = answers.citizenship_status != null
        const selfFollowUpOk = answers.citizenship_status !== 'eu_citizen'
          || (answers.eu_citizen !== null && answers.eu_citizen !== undefined)
        if (!selfAnswered || !selfFollowUpOk) return false

        if (answers.marital_status === 'married') {
          const spouseAnswered = answers.spouse_citizenship_status != null
          const spouseFollowUpOk = answers.spouse_citizenship_status !== 'eu_citizen'
            || (answers.spouse_eu_citizen !== null && answers.spouse_eu_citizen !== undefined)
          return spouseAnswered && spouseFollowUpOk
        }
        return true
      }
      default:
        return true
    }
  }, [currentStep, answers])

  function goNext() {
    if (safeStepIndex < totalSteps - 1) {
      setCurrentStepIndex(safeStepIndex + 1)
    }
  }

  function goBack() {
    if (safeStepIndex > 0) {
      setCurrentStepIndex(safeStepIndex - 1)
    }
  }

  // ── Eligibility checks (per-field) ──────────────────────────────────────
  // Each disqualifying condition is checked independently per field rather
  // than as one combined object, so the eligibility screen can show an
  // inline alert next to the specific row that's the cause — instead of
  // navigating to a separate block screen that loses context on the other
  // two rows when the user goes back.
  const blockedState = useMemo(() => (
    getEligibilityBlock('comunidad', answers)
    || getEligibilityBlock('residency', answers)
    || getEligibilityBlock('beckham_status', answers)
  ), [answers])
  // filing_estimate_mode / dependent_minimum_share used to be derived from
  // the old "Just mine / Combined household" income_scope toggle. That
  // toggle is gone — whose income is whose is now tagged per income item
  // (owner: 'self' | 'spouse'), and filingComparison.ts derives the real
  // individual-vs-joint recommendation from those tags server-side. These
  // are now just safe fallback defaults for the single top-level `result`
  // in the (rare) case filingComparison.ts doesn't run or errors — the
  // edge function overrides `result` with the correct recommended-scenario
  // result whenever the comparison actually applies.
  const filingEstimateMode = 'individual_v1'

  // Married taxpayers are generally entitled to only half of a shared
  // dependent minimum (Art. 58/59 — split equally when both parents
  // qualify) even before we know anything about spouse income specifically.
  const dependentMinimumShare = answers.marital_status === 'married' ? 'half' : 'full'

  const profileFacts = useMemo(() => ({
    citizenships: answers.citizenships,
    // Raw facts, stored as answered.
    citizenship_status: answers.citizenship_status,
    eu_citizen: answers.eu_citizen,
    spouse_citizenship_status: answers.spouse_citizenship_status,
    spouse_eu_citizen: answers.spouse_eu_citizen,
    // Derived conclusions — engine/UI reads these, never re-derives them.
    is_us_person: derivedIsUsPerson,
    spouse_is_us_person: derivedSpouseIsUsPerson,
    marital_status: answers.marital_status,
    filing_status: filingEstimateMode === 'joint_orientation_v1' ? 'joint' : 'individual',
    calculation_mode: filingEstimateMode,
    filing_estimate_mode: filingEstimateMode,
    dependent_minimum_share: dependentMinimumShare,
    age: answers.age !== null && answers.age !== '' ? Number(answers.age) : null,
    spouse_age: answers.spouse_age !== null && answers.spouse_age !== '' ? Number(answers.spouse_age) : null,
    comunidad: answers.comunidad,
    beckham_status: answers.beckham_status,
  }), [answers, filingEstimateMode, dependentMinimumShare, derivedIsUsPerson, derivedSpouseIsUsPerson])

  const taxYearFacts = useMemo(() => ({
    // Always the current bracket year — no longer user-set.
    tax_year: CURRENT_BRACKET_YEAR,
    residency_year_context: answers.residency_year_context,
    days_in_spain: null,
    move_date: null,
  }), [answers.residency_year_context])

  function setAdjustment(field, value) {
    setAdjustmentsForm(a => ({ ...a, [field]: value }))
  }

  function setDependent(field, value) {
    setDependentsForm(d => {
      const next = { ...d, [field]: value }
      // Live-clamp subset fields so the UI reflects what taxMinimums.ts
      // will actually use — children_under_3 <= children_under_25,
      // ascendants_over_75 <= ascendants_over_65.
      if (field === 'children_under_25') {
        const max = Number(value) || 0
        if (Number(next.children_under_3) > max) next.children_under_3 = String(max)
      }
      if (field === 'children_under_3') {
        const max = Number(next.children_under_25) || 0
        if (Number(value) > max) next.children_under_3 = String(max)
      }
      if (field === 'ascendants_over_65') {
        const max = Number(value) || 0
        if (Number(next.ascendants_over_75) > max) next.ascendants_over_75 = String(max)
      }
      if (field === 'ascendants_over_75') {
        const max = Number(next.ascendants_over_65) || 0
        if (Number(value) > max) next.ascendants_over_75 = String(max)
      }
      return next
    })
  }

  // Build AdjustmentFacts for the session payload — empty strings -> 0
  const adjustments = useMemo(() => ({
    pension_contributions: Number(adjustmentsForm.pension_contributions) || 0,
    quarterly_payments: Number(adjustmentsForm.quarterly_payments) || 0,
    additional_withholdings: Number(adjustmentsForm.additional_withholdings) || 0,
  }), [adjustmentsForm])

  // Build DependentFacts for the session payload — empty strings -> 0
  const dependents = useMemo(() => ({
    children_under_25: Number(dependentsForm.children_under_25) || 0,
    children_under_3: Number(dependentsForm.children_under_3) || 0,
    ascendants_over_65: Number(dependentsForm.ascendants_over_65) || 0,
    ascendants_over_75: Number(dependentsForm.ascendants_over_75) || 0,
    disability_taxpayer: !!dependentsForm.disability_taxpayer,
  }), [dependentsForm])

  // ── FX rate helpers ────────────────────────────────────────────────────────

  // Currencies actually present in the current income items (non-EUR only).
  const detectedCurrencies = useMemo(() => {
    const currencies = new Set()
    for (const item of allIncomeItems) {
      if (item.currency && item.currency.toUpperCase() !== 'EUR') {
        currencies.add(item.currency.toUpperCase())
      }
    }
    return Array.from(currencies)
  }, [allIncomeItems])

  const hasForeignCurrencyIncome = detectedCurrencies.length > 0

  // Build the full fx_rates_used map for the session payload.
  // Only includes currencies actually detected in the current income items.
  const fxRatesUsed = useMemo(() => {
    const rates = {}
    for (const currency of detectedCurrencies) {
      const defaultRate = DEFAULT_FX_RATES[currency] ?? null
      const override    = fxRateOverrides[currency]
      const rateToUse   = override !== undefined ? override : defaultRate
      if (rateToUse === null) continue  // unknown currency — engine will warn
      rates[currency] = {
        rate_to_eur:  rateToUse,
        default_rate: defaultRate ?? rateToUse,
        source:       override !== undefined ? 'user_override' : 'default_planning_rate',
      }
    }
    return rates
  }, [detectedCurrencies, fxRateOverrides])

  // Set a user-overridden rate for one currency.
  // Pass null/undefined to revert to the default.
  function setAutonomoFacts(patch) {
    setAutonomoFactsForm(prev => ({ ...(prev || {}), ...patch }))
  }

  function setFxRate(currency, rate) {
    setFxRateOverrides(prev => {
      const next = { ...prev }
      if (rate === null || rate === undefined || rate === '') {
        delete next[currency.toUpperCase()]
      } else {
        next[currency.toUpperCase()] = Number(rate)
      }
      return next
    })
  }

  // Assembles the full CalculatorSession payload (see calculatorTypes.ts)
  // sent to the calculate-irpf edge function.
  function buildSession() {
    return {
      version: '1',
      profile_facts: profileFacts,
      tax_year_facts: taxYearFacts,
      income_items: allIncomeItems,
      adjustments,
      dependents,
      fx_rates_used: fxRatesUsed,
      // Only included when the autónomo journey has initialized this state —
      // omitted entirely (not null) for pure-IRPF sessions, per the
      // "absent, not null" contract on CalculatorSession.
      ...(autonomoFactsForm ? { autonomo_facts: autonomoFactsForm } : {}),
    }
  }

  // Calls the calculate-irpf edge function and stores the result.
  //
  // authToken (optional): Supabase access_token string.
  //   When provided, the edge function sets owner_user_id + tax_subject_id
  //   on the saved row immediately — it's a draft owned by that user, not
  //   yet published. Anonymous callers omit it — the row is saved the same
  //   way, just without an owner.
  //
  // Draft persistence: sends sessionId (if we have one from a prior call)
  // so the edge function updates that same row instead of inserting a new
  // one. The very first call in a session has no sessionId yet, so it
  // inserts — the response's session_id is then stored for every call
  // after that.
  async function runCalculation({ authToken = null, partnerRef = null, sessionOverrides = null } = {}) {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const session = { ...buildSession(), ...(sessionOverrides || {}) }
      const headers = { 'Content-Type': 'application/json' }
      headers['Authorization'] = authToken
        ? `Bearer ${authToken}`
        : `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`

      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/calculate-irpf`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...session,
          partner_ref: partnerRef,
          session_id: sessionId,
          referrer: getFirstReferrer(),
          device_type: getDeviceType(),
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        // A stale sessionId pointing at a now-published row should never
        // happen from this hook's own draft flow, but if it does, fail
        // loudly rather than silently losing the calculation.
        throw new Error(data.error || 'Failed to calculate your estimate. Please try again.')
      }
      setResult(data.result)
      setAutonomoResult(data.autonomo_result ?? null)
      setFilingComparison(data.filing_comparison ?? null)
      setAutonomoIrpfImpact(data.autonomo_irpf_impact ?? null)
      if (data.session_id) setSessionId(data.session_id)
      posthog.capture('calculator_estimate_generated', {
        tax_year: session.tax_year_facts.tax_year,
        income_item_count: session.income_items.length,
        has_foreign_currency_income: Object.keys(session.fx_rates_used).length > 0,
        is_authenticated: Boolean(authToken),
        has_partner_referral: Boolean(partnerRef),
      })
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // Publishes the current draft with an email attached (anonymous flow).
  // Calls publish-session (not calculate-irpf) — publishing is a separate
  // explicit action from calculating, per the draft/publish model. Returns
  // the public_token for the magic-link redirect URL.
  // Called from PublicSidebar in CalculatorResult.js.
  async function saveSession(email, partnerRef = null) {
    if (!sessionId) throw new Error('No calculation to save yet.')
    const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/publish-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ session_id: sessionId, email, partner_ref: partnerRef }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to save your estimate.')
    setPublished(true)
    setSavedToken(data.public_token)
    return data.public_token
  }

  // Publishes the current draft for an authenticated user (no email
  // needed — they're already identified). Used by the "Save & get link"
  // sidebar button in CalculatorResult.js's OwnedSidebar.
  async function publishSession(authToken) {
    if (!sessionId) throw new Error('No calculation to save yet.')
    const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/publish-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ session_id: sessionId }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to save your estimate.')
    setPublished(true)
    setSavedToken(data.public_token)
    return data.public_token
  }

  return {
    answers,
    set,
    currentStep,
    currentStepIndex: safeStepIndex,
    currentFieldKey,
    currentStep1Order,
    totalSteps,
    isLastStep,
    progressPct,
    canProceed,
    goNext,
    goBack,
    profileFacts,
    filingEstimateMode,
    dependentMinimumShare,
    taxYearFacts,
    blockedState,
    incomeItemsByType,
    setIncomeItems,
    allIncomeItems,
    hasAnyIncomeItems,
    adjustmentsForm,
    setAdjustment,
    adjustments,
    dependentsForm,
    setDependent,
    dependents,
    submitting,
    setSubmitting,
    submitError,
    setSubmitError,
    result,
    setResult,
    autonomoFactsForm,
    setAutonomoFacts,
    autonomoResult,
    setAutonomoResult,
    filingComparison,
    setFilingComparison,
    autonomoIrpfImpact,
    setAutonomoIrpfImpact,
    savedToken,
    buildSession,
    runCalculation,
    saveSession,
    // FX
    detectedCurrencies,
    hasForeignCurrencyIncome,
    fxRatesUsed,
    fxRateOverrides,
    setFxRate,
    // Draft/publish lifecycle
    sessionId,
    // Exposed (not just the internal setter used by runCalculation's own
    // response handling) so a caller can seed an EXISTING draft's id before
    // the first recalculate — e.g. CalculatorPage.js's restoreSession
    // effect, when resuming a draft from IntelligencePage.js's "your
    // calculator scenarios" list. Without this, resuming a draft would
    // restore its answers but keep sessionId null, so the very next
    // recalculate would INSERT a brand new row instead of UPDATing the one
    // the user meant to continue — silently doubling their drafts rather
    // than resuming the one they clicked. Must only ever be seeded with a
    // session that is still a draft (published_at null) — seeding a
    // published session's id here would make the next recalculate hit
    // calculate-irpf's SESSION_IMMUTABLE rejection instead.
    setSessionId,
    published,
    publishSession,
  }
}